/**
 * useRecentFiles — track recently opened workspace files in localStorage.
 *
 * - Stores up to MAX_RECENT files, ordered by most-recently-opened.
 * - Deduplicates by file key (re-opening moves to top).
 * - Syncs across tabs via the storage event.
 */

import { useState, useCallback, useEffect } from "react";
import { WorkspaceFile } from "../client/GatewayClient";

const STORAGE_KEY = "rocky-recent-files";
const MAX_RECENT = 10;

export interface RecentEntry {
  /** S3 key — unique identifier */
  key: string;
  /** Display name */
  name: string;
  /** Last opened timestamp (ms) */
  openedAt: number;
}

function loadRecent(): RecentEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.slice(0, MAX_RECENT);
  } catch {
    return [];
  }
}

function saveRecent(entries: RecentEntry[]): void {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(entries.slice(0, MAX_RECENT)),
    );
  } catch {
    // localStorage full or unavailable — silently ignore
  }
}

export function useRecentFiles() {
  const [recentFiles, setRecentFiles] = useState<RecentEntry[]>(loadRecent);

  // Sync from other tabs (read-only — never writes back to localStorage)
  useEffect(() => {
    function onStorage(e: StorageEvent) {
      if (e.key === STORAGE_KEY) {
        setRecentFiles(loadRecent());
      }
    }
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  /** Record a file open — moves to top if already present. */
  const recordOpen = useCallback((file: WorkspaceFile) => {
    setRecentFiles((prev) => {
      const filtered = prev.filter((r) => r.key !== file.key);
      const entry: RecentEntry = {
        key: file.key,
        name: file.name,
        openedAt: Date.now(),
      };
      const next = [entry, ...filtered].slice(0, MAX_RECENT);
      saveRecent(next);
      return next;
    });
  }, []);

  /** Remove a stale entry (e.g. file deleted from workspace). */
  const removeRecent = useCallback((key: string) => {
    setRecentFiles((prev) => {
      const next = prev.filter((r) => r.key !== key);
      saveRecent(next);
      return next;
    });
  }, []);

  return { recentFiles, recordOpen, removeRecent };
}
